import { mode } from "@/config/env";
import { supabase } from "@/services/supabase/client";
import { Database } from "@/services/supabase/types";
import { uuidv4 } from "@/shared/utils/uuidv4";

type DbImages = Database["public"]["Tables"]["images"];
export type ImageRow = DbImages["Row"];
export type ImageInsert = DbImages["Insert"];
export type ImageUpdate = DbImages["Update"];

export const S3_BUCKET = mode === "development" ? "inkrows-dev" : "inkrows-prod";
export type ImageContext = "profile" | "note";

export interface UploadProfileAvatarParams {
  userId: string;
  originalImg: Blob;
  compressedImg: Blob;
}

export interface DeleteProfileAvatarParams {
  userId: string;
  imageId: string;
}

/**
 * Create an image record in the database
 */
async function createImage(data: Omit<ImageInsert, "id">): Promise<ImageRow> {
  const { data: image, error } = await supabase
    .from("images")
    .insert(data as ImageInsert)
    .select()
    .single();

  if (error) {
    throw error;
  }

  return image as ImageRow;
}

/**
 * Fetch an image record by ID
 */
async function getImage(imageId: string): Promise<ImageRow | null> {
  const { data, error } = await supabase.from("images").select("*").eq("id", imageId).maybeSingle();

  if (error) {
    throw error;
  }

  return data as ImageRow | null;
}

/**
 * Update an image record
 */
async function updateImage(imageId: string, data: ImageUpdate): Promise<ImageRow> {
  const { data: image, error } = await supabase.from("images").update(data).eq("id", imageId).select().single();

  if (error) {
    throw error;
  }

  return image as ImageRow;
}

/**
 * Delete an image record from the database and S3
 */
async function deleteImage(imageId: string): Promise<void> {
  const { data: authSession } = await supabase.auth.getSession();
  if (!authSession?.session) {
    throw new Error("Not authenticated");
  }

  const { error } = await supabase.functions.invoke("manage-s3", { body: { image_id: imageId }, method: "DELETE" });

  if (error) {
    throw error;
  }
}

/**
 * Get a signed URL for uploading to S3
 */
async function getUploadSignedUrl(imageId: string): Promise<{ url: string; expiresIn: number }> {
  const { data, error } = await supabase.functions.invoke("manage-s3", {
    body: { image_id: imageId, action: "upload" },
  });
  if (error) {
    throw error;
  }

  return data;
}

/**
 * Get a signed URL for downloading from S3
 */
async function getDownloadSignedUrl(imageId: string): Promise<{ url: string; expiresIn: number }> {
  const { data, error } = await supabase.functions.invoke("manage-s3", {
    body: { image_id: imageId, action: "download" },
  });

  if (error) {
    throw error;
  }

  return data;
}

/**
 * Upload a file to S3 using the edge function (avoids CORS issues)
 */
async function uploadFileToS3(imageId: string, file: Blob): Promise<void> {
  try {
    const formData = new FormData();
    formData.append("image_id", imageId);
    formData.append("file", file);

    const response = await supabase.functions.invoke("manage-s3", { body: formData });

    if (response.error) {
      throw new Error(`Upload failed: ${response.error.message}`);
    }
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    console.error("Error uploading file to S3:", errorMessage);
    throw error;
  }
}

/**
 * Download a file from S3 using a signed URL
 */
async function downloadFileFromS3(signedUrl: string): Promise<Blob> {
  try {
    const response = await fetch(signedUrl, { method: "GET", credentials: "omit" });

    if (!response.ok) {
      console.error(`S3 download failed: ${response.status}`, response.statusText);
      throw new Error(`Failed to download file from S3: ${response.status} ${response.statusText}`);
    }

    return await response.blob();
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    if (errorMessage.includes("CORS") || errorMessage.includes("NetworkError")) {
      console.error("CORS/Network error during S3 download:", error);
      throw new Error("Unable to download file from S3. Please check your S3 bucket CORS configuration.");
    }
    throw error;
  }
}

/**
 * Upload profile avatar (both original and compressed versions)
 * Creates image records and uploads to S3 via edge function
 */
async function uploadProfileAvatar({
  userId,
  originalImg,
  compressedImg,
}: UploadProfileAvatarParams): Promise<{ originalImageId: string; compressedImageId: string }> {
  // Generate a unique identifier for this avatar set
  const avatarId = uuidv4();

  // Create image record for original avatar
  const originalImageData: Omit<ImageInsert, "id"> = {
    owner_id: userId,
    context: "profile",
    s3_key: `public/profiles/${userId}/${avatarId}`,
    bucket: S3_BUCKET,
    visibility: "public",
    mime_type: originalImg.type,
    size_bytes: originalImg.size,
  };

  const originalImage = await createImage(originalImageData);

  try {
    // Upload original via edge function
    await uploadFileToS3(originalImage.id, originalImg);
  } catch (error) {
    // Clean up database record if upload fails
    await deleteImage(originalImage.id);
    throw error;
  }

  // Create image record for compressed avatar
  const compressedImageData: Omit<ImageInsert, "id"> = {
    owner_id: userId,
    context: "profile",
    s3_key: `public/profiles/${userId}/s-${avatarId}`,
    bucket: S3_BUCKET,
    visibility: "public",
    mime_type: compressedImg.type,
    size_bytes: compressedImg.size,
  };

  const compressedImage = await createImage(compressedImageData);

  try {
    // Upload compressed via edge function
    await uploadFileToS3(compressedImage.id, compressedImg);
  } catch (error) {
    // Clean up both database records and original image if compressed upload fails
    await deleteImage(compressedImage.id);
    await deleteImage(originalImage.id);
    throw error;
  }

  return {
    originalImageId: originalImage.id,
    compressedImageId: compressedImage.id,
  };
}

/**
 * Delete profile avatar (both original and compressed versions)
 */
async function deleteProfileAvatar({ userId, imageId }: DeleteProfileAvatarParams): Promise<void> {
  // Delete the image record and all associated files
  // The image record ID represents the compressed version; we need to find and delete the original too

  const image = await getImage(imageId);
  if (!image) {
    return;
  }

  // Extract the avatar ID from the s3_key (e.g., "public/profiles/{userId}/s-{avatarId}" -> {avatarId})
  const keyParts = image.s3_key.split("/");
  const compressedKey = keyParts[keyParts.length - 1];
  const avatarId = compressedKey.startsWith("s-") ? compressedKey.slice(2) : compressedKey;

  // Find both the original and compressed image records
  const { data: images, error: fetchError } = await supabase
    .from("images")
    .select("id")
    .eq("owner_id", userId)
    .eq("context", "profile")
    .in("s3_key", [`public/profiles/${userId}/${avatarId}`, `public/profiles/${userId}/s-${avatarId}`]);

  if (fetchError) {
    throw fetchError;
  }

  if (images && images.length > 0) {
    for (const img of images) {
      await deleteImage(img.id);
    }
  }
}

/**
 * Get profile avatar as a blob by image ID
 */
async function getProfileAvatarBlob(imageId: string | null): Promise<Blob | null> {
  if (!imageId) return null;

  try {
    const image = await getImage(imageId);
    if (!image) {
      return null;
    }

    // Get signed URL and download
    const downloadUrl = await getDownloadSignedUrl(imageId);
    return await downloadFileFromS3(downloadUrl.url);
  } catch {
    return null;
  }
}

/**
 * Get profile avatar as a URL by image ID (direct S3 URL, no edge function call)
 */
async function getProfileAvatarUrl(imageId: string | null): Promise<string | null> {
  if (!imageId) return null;

  try {
    const image = await getImage(imageId);
    if (!image) {
      return null;
    }

    // Direct public S3 URL (no edge function call)
    return `https://${S3_BUCKET}.s3.amazonaws.com/${image.s3_key}`;
  } catch {
    return null;
  }
}

/**
 * Get all profile avatar images for a user
 */
async function getUserProfileImages(userId: string): Promise<ImageRow[]> {
  const { data: images, error } = await supabase
    .from("images")
    .select("*")
    .eq("owner_id", userId)
    .eq("context", "profile");

  if (error) {
    throw error;
  }

  return (images as ImageRow[]) || [];
}

export const storageFetchers = {
  // Image record management
  createImage,
  getImage,
  updateImage,
  deleteImage,
  // S3 operations
  getUploadSignedUrl,
  getDownloadSignedUrl,
  uploadFileToS3,
  downloadFileFromS3,
  // Profile avatar operations
  uploadProfileAvatar,
  deleteProfileAvatar,
  getProfileAvatarBlob,
  getProfileAvatarUrl,
  getUserProfileImages,
};
